// IP合作二级单「音乐权益」单首歌曲内容组件（由外层 MusicBenefitSongsList 的 Form.List 按歌曲 index 注入渲染）。
// 表格布局：ProTablePlus 外壳，行 = 权益类别分组（MUSIC_BENEFIT_GROUPS），
// 列1「权益类别」= 分组名；列2「字段」= 该组字段（DescriptionsFormItem 渲染，复用「权益明细」字段列样式，
// formItemKey = [songIndex, ...field.key] 挂载到 music_benefit[songIndex].xxx）；列3「回复」+ 列4「意见反馈/说明」
// = 整首歌一个回复（rowSpan 跨全部分组行合并，对齐 BudgetOfferTable 范式）。
// 场景模式：internal=内包（字段可编辑 + 回复只读）/ external=外包（字段只读 + 回复/意见可编辑）。
// 回复 item_key 约定：music_benefit_{songIndex}（section='MUSIC_BENEFIT'，后端 negotiation_items 回显）。
// diff 态暂不支持（后续复用 RoundDiffCell 扩展）。

import React, { useEffect, useMemo } from 'react';
import { get, set } from 'lodash-es';
import { Form, Tag } from '@bedrock/components';
import { ProTablePlus } from '@bedrock/eed-react';
import type { IProTablePlusColumn } from '@bedrock/eed-react/lib/components/ProTablePlus/interface';
import DescriptionsFormItem from '@mpc/common/src/components/DescriptionsFormItem';
import {
  ActionCell,
  ACTION_TAG_COLOR,
  ACTION_TEXT_MAP,
  createRenderBatchEditTitle,
  getOpinionTitle,
  OpinionCell,
} from '../NegotiationTable';
import type {
  NegotiationEditState,
  NegotiationItem,
} from '../NegotiationTable';
import {
  getMusicBenefitItemKey,
  MUSIC_MATERIAL_AUTH_REGION_OPTION_KEY,
  MUSIC_BENEFIT_GROUPS,
  MUSIC_BENEFIT_ITEM_KEY_PREFIX,
  MUSIC_BENEFIT_SECTION,
  MUSIC_REGION_OPTION_MAP_KEY,
} from './config';
import type { MusicBenefitGroup } from './config';

/** 单首歌曲字段渲染上下文（show/required/elementProps 函数求值依赖） */
export interface MusicBenefitTableProps {
  /** 歌曲 index（Form.List field.name），决定 formItemKey 前缀与回复 item_key（music_benefit_{index}） */
  songIndex: number;
  /** 场景模式：external=外包（字段只读 + 回复/意见可编辑）；internal/缺省=内包（字段可编辑 + 回复只读） */
  mode?: 'internal' | 'external';
  /** 字段只读态（internal 由外层 editable 传入；external 恒 true） */
  view?: boolean;
  /** 谈判条款列表（detail.l2_info.negotiation_items，按 music_benefit_{index} 回显回复/意见） */
  items?: NegotiationItem[];
  /** 是否可编辑（mode='external' 时回复/意见可编辑；外包端由外层 editable 传入） */
  editable?: boolean;
  /** 编辑状态（mode='external'；key=本歌曲 item_key） */
  editState?: Record<string, NegotiationEditState>;
  /** 单条编辑变更回调 */
  onEditChange?: (
    itemKey: string,
    patch: Partial<NegotiationEditState>
  ) => void;
  /** 批量操作（预留，与旧实现对齐接受但不使用；歌曲粒度单条回复由 ActionCell 承载） */
  onBatchAction?: (action: string, itemKeys?: string[]) => void;
  /** 是否展示回复/意见反馈（缺省 true） */
  showReplyColumns?: boolean;
  /** 字段渲染上下文：optionMap（发行区域/上架平台动态选项）+ formData/detailData（SFormItem 函数 props 求值用） */
  renderData?: any;
}

const MusicBenefitTable: React.FC<MusicBenefitTableProps> = ({
  songIndex,
  mode = 'internal',
  view = false,
  items = [],
  editable = false,
  editState = {},
  onEditChange,
  showReplyColumns = true,
  renderData,
}) => {
  const form = Form.useFormInstance();
  // 当前歌曲对象（live 值，show/required/elementProps 联动谓词读取；初始 undefined 兜底空对象）
  const song = Form.useWatch(['music_benefit', songIndex], form) ?? {};
  // 产品行数据来源优先级：表单实时值（内包编辑态产品行可改）→ 详情派生值（外包只读态）；
  // 以「存在已选产品行」判定数据就绪，避免产品列表未回填时误清空已选授权区域
  const productRows = useMemo(() => {
    const productSources = [
      renderData?.formData?.products,
      renderData?.detailData?.products,
    ];
    return productSources.find(
      (list: any) => Array.isArray(list) && list.some((p: any) => p?.id)
    ) as any[] | undefined;
  }, [renderData]);
  // 「素材所需授权区域」下拉范围 = 产品列表已选的「联动所属区域」（各行 linked_release_region 合并去重），
  // 名称经 Apollo 发行区域选项翻译（镜像 ProductInfoTable「联动所属区域」列）；不再从一级单发行区域自动带入默认值。
  const materialAuthRegionOptions = useMemo(() => {
    const regionCodes: string[] = [];
    (productRows || []).forEach((product: any) => {
      (product?.linked_release_region || []).forEach((code: string) => {
        if (code != null && !regionCodes.includes(code)) regionCodes.push(code);
      });
    });
    const regionNameMap: Record<string, string> = {};
    (renderData?.optionMap?.[MUSIC_REGION_OPTION_MAP_KEY] || []).forEach(
      (it: any) => {
        if (it?.value != null) regionNameMap[it.value] = it?.name ?? it.value;
      }
    );
    return regionCodes.map((code) => ({
      name: regionNameMap[code] ?? code,
      value: code,
    }));
  }, [productRows, renderData]);
  // 字段渲染上下文：formData 注入当前歌曲对象，optionMap/detailData 透传外层（SFormItem configFunctionToValue 求值函数 props）
  const songRenderData = useMemo(
    () => ({
      ...renderData,
      formData: song,
      // 素材所需授权区域选项经 optionMap 注入（与发行区域/上架平台选项同通道）
      optionMap: {
        ...renderData?.optionMap,
        [MUSIC_MATERIAL_AUTH_REGION_OPTION_KEY]: materialAuthRegionOptions,
      },
    }),
    [renderData, song, materialAuthRegionOptions]
  );
  // 联动字段显隐清空：当某字段的 show 条件不满足（显示变隐藏）时，清空该字段已填值。
  // 对齐 PRD「…则显示，否则不显示」——隐藏字段不应残留旧值（如营销端切到产品端后宣传视频数量等）。
  // 仅编辑态（view=false）执行；只读态保留后端回填值。
  useEffect(() => {
    if (view) return;
    const next = { ...song, material_auth_region: { ...(song?.material_auth_region || {}) } };
    let changed = false;
    // 单选 select 清空用 undefined（避免提交数组），多选 select 清空用 []（elementProps.multiple === true 标识）
    const clearValue = (componentClass?: string, multiple?: boolean) => {
      if (componentClass === 'select') return multiple ? [] : undefined;
      if (componentClass === 'inputNumber') return undefined;
      return '';
    };
    MUSIC_BENEFIT_GROUPS.forEach((group) => {
      group.fields.forEach((field) => {
        const show =
          typeof field.show === 'function'
            ? field.show(songRenderData)
            : (field.show ?? true);
        if (show) return;
        const elementProps =
          typeof field.elementProps === 'function'
            ? field.elementProps(songRenderData)
            : field.elementProps;
        const isMultiple = elementProps?.multiple === true;
        const path = field.key;
        const val = get(next, path);
        const isEmpty =
          val == null ||
          val === '' ||
          (Array.isArray(val) && val.length === 0);
        if (isEmpty) return;
        set(next, path, clearValue(field.componentClass, isMultiple));
        changed = true;
      });
    });
    if (changed) {
      form.setFieldValue(['music_benefit', songIndex], next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songRenderData, song, songIndex, view, form]);
  // 素材所需授权区域联动删除：产品列表「联动所属区域」删除某项后，本表已选值同步删除该项（选项由 productRows 派生，天然跟随）。
  // 仅编辑态执行；只读态保留后端回填值（历史单据可能存有超出联动范围的值，展示由字段 showComponent 兜底翻译）。
  useEffect(() => {
    if (view) return;
    if (!productRows?.length) return;
    const allowedCodes = materialAuthRegionOptions.map((it) => it.value);
    const region = song?.material_auth_region || {};
    const next = { ...region };
    let changed = false;
    (['product', 'marketing'] as const).forEach((key) => {
      const cur = region[key];
      if (!Array.isArray(cur) || cur.length === 0) return;
      const kept = cur.filter((code: string) => allowedCodes.includes(code));
      if (kept.length === cur.length) return;
      next[key] = kept;
      changed = true;
    });
    if (changed) {
      form.setFieldValue(
        ['music_benefit', songIndex, 'material_auth_region'],
        next
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialAuthRegionOptions, productRows, song, songIndex, view, form]);
  // 本歌曲回复 item_key（后端约定：music_benefit_{index}，section='MUSIC_BENEFIT'；统一走 config 常量避免前端散落）
  const itemKey = getMusicBenefitItemKey(songIndex);
  // 详情条款按 item_key 索引（仅 MUSIC_BENEFIT 区段 / music_benefit_ 前缀，回显本歌曲 action/opinion）
  const itemsByKey = useMemo(() => {
    const map: Record<string, NegotiationItem> = {};
    (items || [])
      .filter(
        (i) =>
          i.section === MUSIC_BENEFIT_SECTION ||
          i.item_key?.startsWith(MUSIC_BENEFIT_ITEM_KEY_PREFIX)
      )
      .forEach((i) => {
        if (i.item_key) map[i.item_key] = i;
      });
    return map;
  }, [items]);
  // 外包模式：回复列表头批量编辑 Popover（对齐 BudgetOfferTable / CoreTermTable）
  const renderBatchEditTitle = createRenderBatchEditTitle({ editable });

  // 字段单元格：该组当前可见字段，show/required/elementProps 谓词求值后打包 DescriptionsFormItem（复用权益明细样式：
  // label 170px 左对齐、控件列自适应、行间分隔线、行高统一），对齐页面「权益明细」模块字段列。
  const renderFieldsCell = (group: MusicBenefitGroup) => {
    const items = group.fields
      .map((field) => {
        const show =
          typeof field.show === 'function'
            ? field.show(songRenderData)
            : (field.show ?? true);
        if (!show) return null;
        const required =
          typeof field.required === 'function'
            ? field.required(songRenderData)
            : field.required;
        const elementProps =
          typeof field.elementProps === 'function'
            ? field.elementProps(songRenderData)
            : field.elementProps;
        return {
          label: field.label,
          formItemKey: [songIndex, ...field.key],
          componentClass: field.componentClass,
          required,
          view,
          // 字段附属提示（如音乐使用时长/主体音/素材制作周期 tip）：注入 elementProps 由控件 Tooltip 承载
          elementProps: field.tip
            ? { ...elementProps, tip: field.tip }
            : elementProps,
          renderData: songRenderData,
        };
      })
      .filter(Boolean);
    if (!items.length) return <span className="text-color-3 pl-4">--</span>;
    return (
      <DescriptionsFormItem
        className="ip-cooperation-benefit-config"
        column={1}
        items={items}
      />
    );
  };

  // 回复/意见列 rowSpan：整首歌一个回复（跨全部权益分组行合并，对齐 BudgetOfferTable 预算总金额列合并模式）
  const replyOnCell = (_record: unknown, index: number) => {
    if (index === 0)
      return {
        rowSpan: MUSIC_BENEFIT_GROUPS.length,
        style: { verticalAlign: 'middle' },
      };
    return { rowSpan: 0 };
  };

  const columns: IProTablePlusColumn<MusicBenefitGroup>[] = [
    {
      title: '权益类别',
      dataIndex: 'group_name',
      // 对齐「权益明细」权益名称列宽（其实际渲染 155px：列宽 120/130 + 最长权益名撑开）
      width: mode === 'external' ? 130 : `120px`,
      render: (v: string) => (
        <span className="font-medium text-color-1">{v}</span>
      ),
    },
    {
      title: '权益说明',
      dataIndex: 'fields',
      onCell: () => ({ className: 'p-0' }),
      width: mode === 'external' ? 570 : view ? `520px` : 490,
      render: (_v: unknown, row: MusicBenefitGroup) => renderFieldsCell(row),
    },
    {
      title: mode === 'external' ? renderBatchEditTitle([itemKey]) : '回复',
      dataIndex: 'action',
      width: editable && mode === 'external' ? 180 : `100px`,
      onCell: replyOnCell,
      render: (_v: unknown, _row: MusicBenefitGroup, index: number) => {
        if (index !== 0) return null;
        if (mode === 'external') {
          return (
            <ActionCell
              row={{ item_key: itemKey }}
              itemsByKey={itemsByKey}
              editState={editState}
              editable={editable}
              readOnly={!editable}
              onEditChange={onEditChange}
            />
          );
        }
        const action = itemsByKey[itemKey]?.action || '';
        return action ? (
          <Tag
            text={ACTION_TEXT_MAP[action] || action || '-'}
            color={ACTION_TAG_COLOR[action] || 'light-grey'}
          />
        ) : (
          <span className="text-color-3">--</span>
        );
      },
    },
    {
      title: getOpinionTitle(),
      dataIndex: 'opinion',
      ...(mode === 'external' ? { minWidth: 100 } : { width: `400px` }),
      onCell: replyOnCell,
      render: (_v: unknown, _row: MusicBenefitGroup, index: number) => {
        if (index !== 0) return null;
        if (mode === 'external') {
          return (
            <OpinionCell
              row={{ item_key: itemKey }}
              itemsByKey={itemsByKey}
              editState={editState}
              editable={editable}
              readOnly={!editable}
              onEditChange={onEditChange}
            />
          );
        }
        return (
          <span className="whitespace-pre-line text-color-1">
            {itemsByKey[itemKey]?.opinion || '--'}
          </span>
        );
      },
    },
  ];

  const visibleColumns = showReplyColumns ? columns : columns.slice(0, 2);

  return (
    <ProTablePlus<MusicBenefitGroup>
      key={`${mode}-${view ? 'view' : 'edit'}`}
      rowKey="group_key"
      verticalBorder
      columns={visibleColumns}
      data={MUSIC_BENEFIT_GROUPS}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );
};

export default MusicBenefitTable;
